<script>
  import ToggleSwitch from "@/components/widgets/ToggleSwitch.vue";
  import ModuleRowVue from "@/components/ModuleRow/ModuleRow.vue";
  import { getAllowedActionsForModule, getDefaultActionsForModule, getSubmodulesForApp } from "@/config/moduleActions";

  export default {
    props: ["app", "selectApp"],
    components: { ToggleSwitch, ModuleRowVue },
    data() {
      return {
        roles: this.$global.dictionary.roles_es,
        rolesOptions: [],
        modules: [],
        showModules: false,
      };
    },
    computed: {
      isSuperRole() {
        return ["super", "superadmin"].includes(this.app?.role);
      },
      hasActiveModules() {
        return this.modules.some((module) => Array.isArray(module.actions) && module.actions.length);
      },
      showModulesControls() {
        return !!this.app?.role && !this.isSuperRole && this.modules.length > 0 && this.app?.key === "statistics";
      },
      rolesToShow() {
        const isRrhhApp = this.app.key === "rrhh";
        if (isRrhhApp) {
          return {
            empleado: "employee",
            superadmin: "super",
            contabilidad: "accounting",
            jefe: "arealead",
            rrhh: "rrhh",
          };
        }
        return this.roles;
      },

      rolesOptionsToShow() {
        const isRrhhApp = this.app.key === "rrhh";
        if (isRrhhApp) {
          return [
            { name: "Empleado", key: "employee" },
            { name: "Superadmin", key: "super" },
            { name: "Contabilidad", key: "accounting" },
            { name: "Jefe de Área", key: "arealead" },
            { name: "RRHH", key: "rrhh" },
          ];
        }
        return this.rolesOptions;
      },
    },
    watch: {
      "app.role"(newVal, oldVal) {
        if (newVal === oldVal) return;
        if (this.isSuperRole) {
          this.applySuperPermissions();
          this.app.isSelected = true;
          this.selectApp(this.app.key, true, newVal);
          this.syncAppModules();
          if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key });
          return;
        }

        const shouldReset = !this.hasActiveModules;
        this.ensureDefaultModuleActions(shouldReset);
        if (this.app.isSelected) {
          this.syncAppModules();
          if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key });
        }
      },
    },
    methods: {
      shouldEmitPermissionsChange() {
        try {
          const subs = getSubmodulesForApp(this.app?.key);
          return Array.isArray(subs) && subs.length > 0;
        } catch (e) {
          return false;
        }
      },
      getModuleKeyById(id) {
        const mod = this.modules.find((m) => m.id === id);
        return mod ? mod.module || mod.id : id;
      },
      createRolesOptions() {
        return Object.entries(this.roles).map(([name, key]) => ({ name, key }));
      },
      selectOptionApp(event) {
        const { key, role } = this.app;
        const toggled = event?.target?.checked;

        if (typeof toggled === "boolean") {
          this.app.isSelected = toggled;
        }

        const setSelected = !role ? false : this.app.isSelected;

        this.selectApp(key, setSelected, role);

        if (!this.app.isSelected) {
          this.modules = this.modules.map((module) => ({ ...module, actions: [] }));
          this.syncAppModules();
          if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key, remove: true, permissionId: this.app.permissionId });
          return;
        }

        if (this.app.isSelected && this.isSuperRole) {
          this.applySuperPermissions();
          this.syncAppModules();
          if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key });
          return;
        }

        if (this.app.isSelected) {
          this.ensureDefaultModuleActions(!this.hasActiveModules);
          this.syncAppModules();
          if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key });
        }
      },
      selecTest() {
        const { role } = this.app;

        this.app.isSelected = !!role;

        this.selectOptionApp();
        if (this.app.isSelected) {
          if (this.isSuperRole) {
            this.applySuperPermissions();
            this.syncAppModules();
          } else {
            this.ensureDefaultModuleActions(!this.hasActiveModules);
          }
        }
      },
      buildModules() {
        const seedSubmodules = getSubmodulesForApp(this.app?.key);
        const sourceModules = seedSubmodules.length ? seedSubmodules : [this.buildFallbackModule()];

        const normalizeKey = (m) => (m?.module || m?.key || m?.title || m?.name || "").toString().trim().toLowerCase();

        const getModuleKey = (m, index) => (m?.module || m?.key || m?.title || m?.name || `module-${index}`).toString();

        const existingModulesByKey = Object.fromEntries((Array.isArray(this.app?.modules) ? this.app.modules : []).map((m) => [normalizeKey(m), m]).filter(([k]) => k));

        return sourceModules.map((mod, index) => {
          const moduleKey = getModuleKey(mod, index);
          const existing = existingModulesByKey[moduleKey.toLowerCase()];

          const rawActions = existing?.actions ?? mod?.actions;

          const actions = this.normalizeModuleActions({
            ...mod,
            module: moduleKey,
            actions: rawActions,
          });

          return {
            id: mod?.id ?? moduleKey ?? index,
            module: moduleKey,
            title: mod?.title || mod?.name || mod?.module || this.app?.name || this.app?.key || `module-${index}`,
            icon: mod?.icon || mod?.module || this.app?.key || "chart",
            actions,
          };
        });
      },
      buildFallbackModule() {
        const key = this.app?.key || "module";
        return {
          id: `${key}-0`,
          module: key,
          title: this.app?.name || key,
          icon: this.app?.icon || key,
          actions: [],
        };
      },

      normalizeModuleActions(module) {
        const moduleKey = module.module || module.key || module.title || module.name;
        const allowed = getAllowedActionsForModule(moduleKey);
        const defaults = getDefaultActionsForModule(moduleKey);

        const hasActionsProp = Array.isArray(module.actions);
        const rawActions = hasActionsProp ? module.actions : [];
        const toggleActions = rawActions.filter((action) => ["enable", "disable"].includes(action));
        const filtered = rawActions.filter((action) => allowed.includes(action));

        const baseActions = hasActionsProp ? filtered : defaults.filter((action) => allowed.includes(action) || ["enable", "disable"].includes(action));

        return Array.from(new Set([...baseActions, ...toggleActions]));
      },
      applySuperPermissions() {
        this.modules = this.modules.map((module) => {
          const allowed = getAllowedActionsForModule(module.module);
          const actions = Array.from(new Set([...allowed, "enable"]));
          return { ...module, actions };
        });
      },
      ensureDefaultModuleActions(forceReset = false) {
        this.modules = this.modules.map((module) => ({
          ...module,
          actions: forceReset ? [] : this.normalizeModuleActions(module),
        }));
        this.syncAppModules();
      },
      ensureSelectedForModules() {
        if (!this.app?.role) return;
        if (this.app.isSelected === false) return;
        if (this.app.isSelected === true) return;
        this.app.isSelected = true;
        this.selectApp(this.app.key, true, this.app.role);
      },
      syncAppModules() {
        if (!this.app) return;
        this.app.modules = this.modules.map(({ module, actions, icon }) => ({
          module,
          actions,
          icon,
        }));
      },
      toggleModuleAction({ id, action }) {
        this.ensureSelectedForModules();
        const allowed = getAllowedActionsForModule(this.modules.find((module) => module.id === id)?.module);
        if (!allowed.includes(action)) return;

        this.modules = this.modules.map((module) => {
          if (module.id !== id) return module;
          const hasAction = module.actions.includes(action);
          const actions = hasAction ? module.actions.filter((item) => item !== action) : [...module.actions, action];
          return { ...module, actions };
        });

        this.syncAppModules();
        const moduleKey = this.getModuleKeyById(id);
        if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key, module: moduleKey, action });
      },
      toggleModuleEnable({ id, checked }) {
        this.ensureSelectedForModules();
        this.modules = this.modules.map((module) => {
          if (module.id !== id) return module;
          let actions = module.actions.filter((action) => !["enable", "disable"].includes(action));
          actions = checked ? [...actions, "enable"] : [...actions, "disable"];
          return { ...module, actions };
        });
        this.syncAppModules();
        const moduleKey = this.getModuleKeyById(id);
        if (this.shouldEmitPermissionsChange()) this.$emit("permissions-change", { appKey: this.app.key, module: moduleKey, enabled: checked });
      },
      openDownwards() {
        this.showModules = !this.showModules;
      },
    },
    created() {
      this.modules = this.buildModules();

      if (this.isSuperRole) {
        this.applySuperPermissions();
      } else {
        this.ensureDefaultModuleActions(!this.hasActiveModules);
      }
    },
    mounted() {
      this.rolesOptions = this.createRolesOptions();
    },
  };
</script>

<template>
  <section :class="['applicationCard', { 'applicationCard--active': showModules }]">
    <div class="applicationCard__left">
      <img class="applicationCard__logo" :src="app.icon" alt="logo" />
      <p class="applicationCard__appName">{{ app.name }}</p>
    </div>

    <div class="applicationCard__center">
      <select class="applicationCard__input" v-model="app.role" @change="selecTest">
        <option value="">Seleccione</option>
        <option v-for="role in rolesOptionsToShow" :value="role.key" :key="role.key">
          {{ role.name }}
        </option>
      </select>
    </div>

    <div class="applicationCard__right">
      <ToggleSwitch :checked="app.isSelected" @change="selectOptionApp" :disabled="!app.role" ref="toggle" class="toggle" />
      <iconic
        v-if="showModulesControls && app.role"
        :name="'downArrow'"
        :class="['applicationCard__downArrow', { 'applicationCard__downArrow--open': showModules }]"
        :aria-expanded="showModules"
        role="button"
        tabindex="0"
        @click.native="openDownwards"
        @keyup.enter.space="openDownwards" />
    </div>
    <transition name="collapse">
      <div class="applicationCard__modules" v-if="showModules && showModulesControls">
        <ModuleRowVue v-for="module in modules" :key="module.id" :module="module" @toggle-action="toggleModuleAction" @toggle-enable="toggleModuleEnable" />
      </div>
    </transition>
  </section>
</template>

<style lang="scss">
  .applicationCard {
    display: grid;
    grid-template-columns: minmax(160px, 1fr) minmax(120px, 1.5fr) auto;
    width: 100%;
    gap: 12px;
    padding: $mpadding;
    align-items: center;
    column-gap: 20px;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.18);
    &--active {
      border: 1px solid #d0d0d0;
    }

    &__left {
      @include Flex(row, flex-start, center);
      gap: $mpadding;
    }

    &__center {
      justify-self: center;
      font-family: $newFont_regular;
      width: 100%;

      max-width: 170px;
    }

    &__right {
      @include Flex(row, flex-end, center);
      gap: $mpadding;
    }

    &__logo {
      width: 25px;
      height: 25px;
      border-radius: 2px;
    }

    &__appName {
      text-transform: capitalize;
      font-family: $newFont_regular;
      color: #000;
      font-size: 16px;
    }

    &__input {
      width: 100%;
      height: 35px;
      padding: 5px;
      text-transform: capitalize;

      border-radius: 8px;
      border: 1px solid #e5e5e5;
      background: #f9f9f9;

      &:focus {
        outline: none;
      }
    }

    &__downArrow {
      cursor: pointer;
      transition: transform 0.2s ease;

      &--open {
        transform: rotate(180deg);
      }
    }

    &__modules {
      grid-column: 1 / -1;
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 12px;
    }

    .collapse-enter-active,
    .collapse-leave-active {
      transition: all 0.2s ease;
      max-height: 500px;
      opacity: 1;
    }

    .collapse-enter,
    .collapse-leave-to {
      max-height: 0;
      opacity: 0;
      overflow: hidden;
    }

    .toggleSwitch-switch {
      width: 36px;
      height: 20px;
      &::before {
        top: 3px;
        width: 14px;
        height: 14px;
      }
    }
    .toggleSwitch-checkbox:checked + .toggleSwitch-switch:before {
      left: 18px;
    }

    .mycheck {
      width: 15px;
      height: 15px;
      padding: 9px;
      border-radius: 50%;
      &:checked {
        background-color: $primary-color;
        border-color: $primary-color;
      }
    }
  }
</style>
